import { AbstractBossService } from '@abstract-services/boss/boss.abstract-service';
import { Injectable } from '@angular/core';
import * as ngCommonHttp from '@angular/common/http';
import { HttpHeaders } from '@angular/common/http';
import {
  catchError,
  concat,
  filter,
  map,
  Observable,
  of,
  mergeMap,
  tap,
  throwError,
  switchMap,
  take,
} from 'rxjs';

import { environment } from 'environments/environment';
import {
  BossItem,
  BossItemMapping,
  BossItemStoreMap,
  BossStore,
  BossTenantInfo,
} from '@interfaces/boss/1.0';
import { BrandService } from '@services/brand/1.0/brand.service';
import { Tenant } from '@generated/tcea/models/tenant';

@Injectable({
  providedIn: 'root',
})
export class BossService implements AbstractBossService {
  apiUrl: string;
  headers: HttpHeaders;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private brandService: BrandService,
  ) {
    this.apiUrl = environment.bossApi;
    this.brandService.currentBrandBehaviorSubject
      .pipe(
        filter((brand: Tenant) => brand.isSet),
        map((brand) => {
          this.headers = new HttpHeaders({ 'Tenant-Code': brand.alias + brand.countryCode });
        }),
      )
      .subscribe();
  }

  getItemAssociations(itemId: string): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/items/${itemId}/mappings`, { headers }).pipe(
            map((response: BossItemMapping[]) => {
              return response;
            }),
            take(1),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  createItemAssociation(parentId: string, item: BossItemMapping): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .post(`${this.apiUrl}/items/${parentId}`, item, {
              headers,
              responseType: 'text' as 'json',
            })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  deleteItemAssociation(parentId: string, childId: string): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.delete(`${this.apiUrl}/items/${parentId}/${childId}`, { headers }).pipe(
            map((response: BossItemMapping[]) => {
              return response;
            }),
            take(1),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  updateItemAssociation(parentId: string, item: BossItemMapping): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .patch(`${this.apiUrl}/items/${parentId}/${item.itemId}`, item, { headers })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  getStoreItemAssociations(storeId: string, itemId: string): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .get(`${this.apiUrl}/stores/${storeId}/items/${itemId}`, { headers })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  createStoreItemAssociation(
    storeId: string,
    parentId: string,
    item: BossItemMapping,
  ): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .post(`${this.apiUrl}/stores/${storeId}/items/${parentId}/${item.itemId}`, item, {
              headers,
              responseType: 'text' as 'json',
            })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  deleteStoreItemAssociation(
    storeId: string,
    parentId: string,
    childId: string,
  ): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .delete(`${this.apiUrl}/stores/${storeId}/items/${parentId}/${childId}`, { headers })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  updateStoreItemAssociation(
    storeId: string,
    parentId: string,
    item: BossItemMapping,
  ): Observable<BossItemMapping[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .patch(`${this.apiUrl}/stores/${storeId}/items/${parentId}/${item.itemId}`, item, {
              headers,
            })
            .pipe(
              map((response: BossItemMapping[]) => {
                return response;
              }),
              take(1),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  getHeaders(): Observable<HttpHeaders> {
    if (this.headers) {
      return of(this.headers);
    } else {
      return this.brandService.currentBrandBehaviorSubject.pipe(
        filter((brand: Tenant) => brand.isSet),
        map((brand) => {
          return new HttpHeaders({ 'Tenant-Code': brand.alias + brand.countryCode });
        }),
        tap((headersParam) => {
          this.headers = headersParam;
        }),
      );
    }
  }

  // #region STORES
  getStores(): Observable<BossStore[]> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/stores/`, { headers }).pipe(
            map((response) => {
              return response as BossStore[];
            }),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  addStore(store): Observable<Object> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          const body = store;
          return this.http.post(`${this.apiUrl}/stores/`, body, { headers });
        } else {
          return of(undefined);
        }
      }),
    );
  }

  getStore(storeId: string): Observable<BossStore> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/stores/${storeId}/`, { headers }).pipe(
            map((response) => {
              return response as BossStore;
            }),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  patchStore(storeId: string, store: Partial<BossStore>): Observable<BossStore> {
    const id = storeId;
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.patch(`${this.apiUrl}/stores/${id}/`, store, { headers }).pipe(
            map((response) => {
              return response as BossStore;
            }),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  deleteStore(storeId: string): Observable<Object> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.delete(`${this.apiUrl}/stores/${storeId}/`, { headers });
        } else {
          return of(undefined);
        }
      }),
    );
  }

  getTenant(brandAlias: string): Observable<BossTenantInfo> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/tenants/${brandAlias}`, { headers }).pipe(
            map((response) => {
              return response as BossTenantInfo;
            }),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  createTenant(body: BossTenantInfo): Observable<BossTenantInfo> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.post(`${this.apiUrl}/tenants`, body, { headers }).pipe(
            map((response) => {
              return response as BossTenantInfo;
            }),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  // #region POS ITEMS/ENTITIES
  getItems(): Observable<BossItem[]> {
    return this.getHeaders().pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/items`, { headers }).pipe(
            map((response) => {
              return response as BossItem[];
            }),
            take(1),
          );
        } else {
          return of([] as BossItem[]);
        }
      }),
    );
  }

  getItem(id: string): Observable<BossItem> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/items/${id}`, { headers }).pipe(
            map((response) => {
              return response as BossItem;
            }),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  getItemStores(id: string): Observable<BossItemStoreMap[]> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/items/${id}/storemappings`, { headers }).pipe(
            map((response) => {
              return response as BossItemStoreMap[];
            }),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  addItem(item: BossItem): Observable<Object> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.post(`${this.apiUrl}/items`, item, { headers });
        } else {
          return of(undefined);
        }
      }),
    );
  }

  updateItem(item: Partial<BossItem>, itemId: string): Observable<Object> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.patch(`${this.apiUrl}/items/${itemId}`, item, { headers });
        } else {
          return of(undefined);
        }
      }),
    );
  }

  //delete item by itemId
  deleteItem(itemId: string): Observable<any> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.delete(`${this.apiUrl}/items/${itemId}`, { headers }).pipe(
            map((response) => {
              return of(response);
            }),
            catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(() => err)),
          );
        } else {
          return of(undefined);
        }
      }),
    );
  }

  getItemByStore(storeId): Observable<BossItem[]> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http.get(`${this.apiUrl}/stores/${storeId}/items`, { headers }).pipe(
            map((response) => {
              return response as BossItem[];
            }),
          );
        } else {
          return of([]);
        }
      }),
    );
  }

  updateItemByStore(storeId, itemId, body): Observable<BossItem[]> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .post(`${this.apiUrl}/stores/${storeId}/items/${itemId}`, body, { headers })
            .pipe(
              map((response) => {
                return response as BossItem[];
              }),
            );
        } else {
          return of([]);
        }
      }),
    );
  }

  //delete store item association
  deleteItemByStore(storeId, itemId): Observable<BossItem[]> {
    return concat(this.getHeaders()).pipe(
      mergeMap((headers) => {
        if (headers) {
          return this.http
            .delete(`${this.apiUrl}/stores/${storeId}/items/${itemId}`, { headers })
            .pipe(
              map((response) => {
                return response as BossItem[];
              }),
            );
        } else {
          return of([]);
        }
      }),
    );
  }
}
